import { useEffect, useState } from "react";
import { useApp } from "@/store/app.js";
import { config, configCheck } from "@/lib/config.js";
import { getSession, clearSession } from "@/lib/session.js";
import { clearDek } from "@/lib/dek.js";
import { identifyUser, clearUser } from "@/lib/observability.js";
import { useNavigate } from "react-router-dom";
import { formatTier } from "@/lib/tier-format.js";
import { Button } from "@/components/ui/Button.js";
import { GA4Panel } from "./panels/GA4Panel.js";
import { GSCPanel } from "./panels/GSCPanel.js";
import { GoogleAdsPanel } from "./panels/GoogleAdsPanel.js";
import { MetaAdsPanel } from "./panels/MetaAdsPanel.js";
import { CSVPanel } from "./panels/CSVPanel.js";
import { DrivePanel } from "./panels/DrivePanel.js";
import { HostnamesPanel } from "./panels/HostnamesPanel.js";
import { EmailDeliveryPanel } from "./panels/EmailDeliveryPanel.js";
import { SchedulesPanel } from "./panels/SchedulesPanel.js";
import { CustomCssPanel } from "./panels/CustomCssPanel.js";
import { TeamPanel } from "./panels/TeamPanel.js";

export function Dashboard() {
  const { user } = useApp();
  const [err, setErr] = useState<string | undefined>();
  const cfg = configCheck();
  const navigate = useNavigate();
  const session = getSession();
  const displayEmail = session?.payload.email ?? user.email;
  const tierLabel = formatTier(session?.payload.tier);

  // Sync the Zustand `user.email` from the session so existing panels see it.
  // Also identify the user to Sentry + PostHog on every session change.
  useEffect(() => {
    if (!session) return;
    if (session.payload.email !== user.email) {
      useApp.setState({ user: { email: session.payload.email } });
    }
    identifyUser(
      session.payload.email,
      session.payload.tier == null ? undefined : String(session.payload.tier),
    );
  }, [session, user.email]);

  function onLogout() {
    clearUser();
    clearDek();
    clearSession();
    navigate("/login", { replace: true });
  }

  return (
    <div className="mx-auto max-w-5xl p-6">
      <header className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">AgencyLens</h1>
          <p className="text-sm text-slate-500">
            Connect sources → fetch data → download branded PDF
          </p>
        </div>
        <div className="flex items-center gap-3 text-sm text-slate-500">
          <div className="text-right">
            <div className="font-medium text-slate-700">{displayEmail}</div>
            {tierLabel && (
              <div className="text-xs uppercase tracking-wide text-slate-400">
                {tierLabel}
              </div>
            )}
          </div>
          <Button variant="ghost" size="sm" onClick={onLogout}>
            Sign out
          </Button>
        </div>
      </header>

      {!cfg.ok && (
        <div className="mb-6 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <strong>Config missing:</strong> {cfg.missing.join(", ")}. Set these in
          a <code>.env.local</code> file in <code>apps/web</code> before clicking
          Connect.
        </div>
      )}

      {err && (
        <div className="mb-6 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
          {err}
        </div>
      )}

      <GA4Panel onError={setErr} configReady={cfg.ok} />
      <GSCPanel onError={setErr} configReady={cfg.ok} />
      <GoogleAdsPanel onError={setErr} configReady={cfg.ok} />
      <MetaAdsPanel onError={setErr} />
      <CSVPanel onError={setErr} />
      <DrivePanel onError={setErr} configReady={cfg.ok} />
      <HostnamesPanel onError={setErr} />
      <EmailDeliveryPanel onError={setErr} />
      <SchedulesPanel onError={setErr} />
      <CustomCssPanel onError={setErr} />
      <TeamPanel onError={setErr} />

      <footer className="mt-12 text-xs text-slate-400">
        OAuth redirect: <code>{config.googleOauthRedirectUri}</code>
      </footer>
    </div>
  );
}
